/**
 * iter170 — Bloc de pièces jointes PDF sur un OR.
 * - Liste des attachments existants (nom, taille, source, toggle attach_to_docs, actions).
 * - Upload PDF (max 20 Mo).
 * - Les pièces attach_to_docs=true sont concaténées automatiquement au devis/facture PDF.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Paperclip, Upload, Trash2, Download, FileText, Loader2, Pencil, Check, X, Camera, ChevronDown, Image as ImageIcon, Smartphone } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import OrPhotoLibraryDialog from "@/components/or/OrPhotoLibraryDialog";
import OrPhotoCaptureDialog from "@/components/or/OrPhotoCaptureDialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

const SOURCE_LABELS = {
  "manual": { label: "Ajouté manuellement", color: "bg-blue-50 text-blue-700 border-blue-200" },
  "auto-i-report": { label: "Rapport Auto-I", color: "bg-purple-50 text-purple-700 border-purple-200" },
  "guido-report": { label: "Rapport Guido", color: "bg-teal-50 text-teal-700 border-teal-200" },
};

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function OrAttachmentsBlock({ orId, canEdit = true, vehiculeId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // iter230 — renommage inline d'une pièce jointe
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const galerieRef = useRef(null);
  // iter267 — photos déjà enregistrées dans la bibliothèque « Photos » du CRM
  const [libraryOpen, setLibraryOpen] = useState(false);
  // iter280 — prise de vue multi-pages
  const [captureOpen, setCaptureOpen] = useState(false);

  const onRenameSave = async (att) => {
    const val = (renameValue || "").trim();
    if (!val || val === att.filename) { setRenameId(null); return; }
    try {
      const { data } = await api.patch(`/or/attachments/${att.id}/rename`, { filename: val });
      setItems((prev) => prev.map((x) => (x.id === att.id ? { ...x, filename: data.filename } : x)));
      toast.success(`Renommé en ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renommage impossible");
    } finally {
      setRenameId(null);
    }
  };

  const fetchItems = useCallback(async () => {
    if (!orId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/or/${orId}/attachments`);
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [orId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const onUpload = async (files) => {
    if (!files || files.length === 0) return;
    for (const f of files) {
      if (!f.name.toLowerCase().endsWith(".pdf")) {
        toast.error(`${f.name} — PDF uniquement`);
        continue;
      }
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", f);
        await api.post(`/or/${orId}/attachments/upload?source=manual`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`${f.name} ajouté`);
      } catch (e) {
        toast.error(`Upload échoué : ${e.response?.data?.detail || e.message}`);
      } finally {
        setUploading(false);
      }
    }
    fetchItems();
  };

  // iter260 — photos prises à l'appareil : compression navigateur puis PDF A4 côté serveur
  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX = 1600;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const cv = document.createElement("canvas");
        cv.width = Math.round(img.width * scale);
        cv.height = Math.round(img.height * scale);
        cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
        resolve(cv.toDataURL("image/jpeg", 0.72));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const onPhotos = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    if (list.length > 30) { toast.error("30 photos maximum"); return; }
    setUploading(true);
    try {
      const photos = [];
      for (const f of list) photos.push(await compressImage(f));
      const { data } = await api.post(`/or/${orId}/attachments/from-photos`, { photos });
      toast.success(`${data.attachment.filename} créé (${list.length} page${list.length > 1 ? "s" : ""})`);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création du PDF impossible");
    } finally {
      setUploading(false);
      if (camRef.current) camRef.current.value = "";
    }
  };

  // iter280 — plusieurs clichés à la suite → un seul PDF
  const onPhotosDataUrls = async (photos) => {
    if (!photos?.length) return;
    setUploading(true);
    try {
      const { data } = await api.post(`/or/${orId}/attachments/from-photos`, { photos });
      toast.success(`${data.attachment.filename} créé (${photos.length} page${photos.length > 1 ? "s" : ""})`);
      setCaptureOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création du PDF impossible");
    } finally {
      setUploading(false);
    }
  };

  // iter267 — Photos choisies dans la bibliothèque du CRM → même PDF A4 que l'appareil photo
  const onPhotosCrm = async (photoIds) => {
    setUploading(true);
    try {
      const { data: lib } = await api.post("/photos/data-urls", { photo_ids: photoIds, or_id: orId });
      const photos = (lib.items || []).map((x) => x.data_url);
      const { data } = await api.post(`/or/${orId}/attachments/from-photos`, { photos });
      toast.success(`${data.attachment.filename} créé (${photos.length} page${photos.length > 1 ? "s" : ""})`);
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création du PDF impossible");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (att) => {
    if (!window.confirm(`Supprimer "${att.filename}" ?`)) return;
    try {
      await api.delete(`/or/attachments/${att.id}`);
      toast.success("Pièce supprimée");
      fetchItems();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  const onDownload = async (att) => {
    try {
      const { data: blob } = await api.get(`/or/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(`Téléchargement échoué : ${e.response?.data?.detail || e.message}`);
    }
  };

  const onToggle = async (att) => {
    try {
      await api.patch(`/or/attachments/${att.id}/toggle-attach`, { attach_to_docs: !att.attach_to_docs });
      fetchItems();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  // iter238 — visible sur le lien de suivi public du client
  const onTogglePartage = async (att) => {
    try {
      await api.patch(`/or/attachments/${att.id}/partage`, { share_public: !att.share_public });
      fetchItems();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <Card className="p-4 border-gray-200" data-testid="or-attachments-block">      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-[#52a675]" />
          Pièces jointes PDF ({items.length})
        </h3>
        {canEdit && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={uploading} data-testid="or-attachment-upload-btn">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                  Ajouter
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onClick={() => fileRef.current?.click()} data-testid="or-attachment-pick-pdf">
                  <FileText className="w-4 h-4 mr-2 text-gray-500" /> Choisir un PDF
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-gray-400">
                  Photos
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setCaptureOpen(true)} data-testid="or-attachment-pick-photos">
                  <Camera className="w-4 h-4 mr-2 text-[#3d8a5c]" /> Prendre des photos (PDF)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLibraryOpen(true)} data-testid="or-attachment-pick-library">
                  <ImageIcon className="w-4 h-4 mr-2 text-[#52a675]" /> Photos du CRM
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => galerieRef.current?.click()} data-testid="or-attachment-pick-galerie">
                  <Smartphone className="w-4 h-4 mr-2 text-gray-500" /> Galerie du téléphone
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <input
              ref={camRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => onPhotos(e.target.files)}
              data-testid="or-attachment-camera-input"
            />
            <input
              ref={galerieRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPhotos(e.target.files)}
              data-testid="or-attachment-galerie-input"
            />
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={(e) => onUpload(e.target.files)}
              data-testid="or-attachment-file-input"
            />
          </>
        )}
      </div>
      <OrPhotoLibraryDialog
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        orId={orId}
        vehiculeId={vehiculeId}
        titre="Photos du CRM"
        onPick={onPhotosCrm}
      />
      <OrPhotoCaptureDialog
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        onDone={onPhotosDataUrls}
        busy={uploading}
      />

      <div className="space-y-1">
        {loading && <div className="text-xs text-gray-400 text-center py-4">Chargement…</div>}
        {!loading && items.length === 0 && (
          <div className="text-xs text-gray-400 text-center py-6">Aucune pièce jointe. Ajoutez un PDF ci-dessus.</div>
        )}
        {items.map((att) => {
          const src = SOURCE_LABELS[att.source] || SOURCE_LABELS.manual;
          const isEditing = renameId === att.id;
          return (
          <div key={att.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-100 hover:border-gray-300" data-testid={`or-attachment-${att.id}`}>
            <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <Input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); onRenameSave(att); }
                      if (e.key === "Escape") setRenameId(null);
                    }}
                    className="h-7 text-xs"
                    autoFocus
                    data-testid={`or-attachment-rename-input-${att.id}`}
                  />
                  <Button size="sm" className="h-7 px-2 bg-[#52a675] hover:bg-[#3d8a5c]" onClick={() => onRenameSave(att)} data-testid={`or-attachment-rename-save-${att.id}`}>
                    <Check className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setRenameId(null)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="text-xs font-semibold text-gray-900 truncate">{att.filename}</div>
              )}
              <div className="text-[10px] text-gray-500 flex items-center gap-2">
                <span>{fmtSize(att.size_bytes)}</span>
                <Badge variant="outline" className={`${src.color} text-[9px] py-0`}>{src.label}</Badge>
              </div>
            </div>
            <label className="flex items-center gap-1 text-[10px] cursor-pointer" title="Joindre à l'email (fichier séparé)">
              <input
                type="checkbox"
                checked={!!att.attach_to_docs}
                onChange={() => onToggle(att)}
                data-testid={`or-attachment-toggle-${att.id}`}
              />
              joindre
            </label>
            <label className="flex items-center gap-1 text-[10px] cursor-pointer" title="Visible sur le lien de suivi du client">
              <input
                type="checkbox"
                checked={!!att.share_public}
                onChange={() => onTogglePartage(att)}
                data-testid={`or-attachment-partage-${att.id}`}
              />
              partagée
            </label>
            {canEdit && !isEditing && (
              <button
                type="button"
                onClick={() => { setRenameId(att.id); setRenameValue(att.filename || ""); }}
                className="text-gray-500 hover:text-gray-800"
                title="Renommer"
                data-testid={`or-attachment-rename-${att.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            <button type="button" onClick={() => onDownload(att)} className="text-blue-600 hover:text-blue-800" title="Télécharger">
              <Download className="w-3.5 h-3.5" />
            </button>
            {canEdit && (
              <button type="button" onClick={() => onDelete(att)} className="text-red-600 hover:text-red-800" title="Supprimer" data-testid={`or-attachment-delete-${att.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );})}
      </div>
    </Card>
  );
}
